import React, { useEffect, useRef, useState } from 'react'
import Autocomplete, { AutocompleteItem, AutocompleteItemType } from '../__autocomplete/autocomplete'
import EditText from 'blocks.simple/editText/editText'
import translate from 'core/translate'
import { cn } from 'ethcss'
import styles from './searchbar__search.jcss'
import { isExist } from 'core/utils/coreUtil'
import { getThemeStyleValue } from 'theme/colors'
import { defaultThemeStyles } from 'blocks.simple/icon/icon.theme'
import { connect } from 'react-redux'
import { themeFromStore } from 'core/helpers/menu'
import type { IconProps } from 'blocks.simple/icon/icon'
import type { EditTextInputElement, EditTextProps } from 'blocks.simple/editText/editText.types'
import type { ISearchbarAutocomplete } from '../searchbar'
import { useClickOutside } from 'react-click-outside-hook'

interface IState {
    value: string
    isOpen: boolean
    active: boolean
}
interface ISearchProps {
    id?: string | number
    onClickOutside?: (element: unknown, value: boolean) => void
    onChange: (value: string) => void
    search?: ISearchbarAutocomplete | null
    onFocus?: (value: string) => void
    onClick?: (value: string) => void
    onBlur?: (value: string) => void
    enableMobileSearch?: boolean
    searchIsOpened?: boolean
    placeholder?: string
    view?: unknown
    mod?: string
    className?: string | object
    sort?: unknown
    isMobile?: boolean
    icon?: string
    activeAdvancedSearchIconColor?: string
    iconActiveColor?: string
    iconColor?: string
    onSearchIconClick?: EditTextProps['onIconClick']
    searchToggle?: () => void
    iconTooltip?: IconProps['tooltip']
    value?: string
    rounded?: string
    filter?: unknown
}

const Search = (p_: ISearchProps) => {
    const getValue = (value?: string) => {
        if (!value) {
            return ''
        }

        return value
    }
    const [state, setState] = useState<IState>({
        value: getValue(p_.value),
        isOpen: false,
        active: false,
    })
    const typingRef = useRef(false)
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [searchRef, hasClickedOutside] = useClickOutside()

    useEffect(() => {
        if (!typingRef.current) {
            setState((prevState) => {
                return { ...prevState, value: getValue(p_.value) }
            })
        }
    }, [p_.value])

    useEffect(
        () => () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current)
        },
        []
    )

    useEffect(() => {
        if (!hasClickedOutside) return

        p_.onClickOutside?.(null, false)

        setState((prevState) => {
            return { ...prevState, isOpen: false }
        })
    }, [hasClickedOutside, p_.onClickOutside])

    const changeSearch = (value: string) => {
        typingRef.current = true
        value = getValue(value)

        setState((prevState) => {
            return { ...prevState, value }
        })
        if (timeoutRef.current) clearTimeout(timeoutRef.current)

        timeoutRef.current = setTimeout(() => {
            typingRef.current = false
            if (p_.onChange) {
                p_.onChange(value)
            }
        }, 600)
    }
    const selectItem = (searchItem: AutocompleteItem, type: AutocompleteItemType) => {
        const search = p_.search

        if (!search) return

        setState((prevState) => {
            return { ...prevState, isOpen: false }
        })

        if (type === 'places') {
            search.onSelect({
                ...searchItem,
                type,
            })
        } else if (type === 'displays') {
            search.onSelect({
                ...searchItem,
                id: searchItem.id,
                name: searchItem.name || searchItem.title,
                location: {
                    lat: searchItem.lat,
                    lng: searchItem.lng,
                },
                type,
            })
        } else {
            search.onSelect({
                id: searchItem.id,
                name: searchItem.name || searchItem.title,
                type,
            })
        }
    }
    const clearFilter = (target: { focus: () => void }) => {
        if (state.value) {
            if (p_.onChange) {
                p_.onChange('')
            }
            target.focus()
            setState((prevState) => {
                return { ...prevState, value: '' }
            })
        }
    }
    const onFocus = (value: string) => {
        setState((prevState) => {
            return { ...prevState, active: true }
        })
        if (p_.onFocus) {
            p_.onFocus(value)
        }
        if (p_.search) {
            setState((prevState) => {
                return { ...prevState, isOpen: true }
            })
        }
    }
    const onBlur = (value: string) => {
        setState((prevState) => {
            return { ...prevState, active: false }
        })
        if (p_.onBlur) {
            p_.onBlur(value)
        }
    }
    const onClick = (e: React.MouseEvent<EditTextInputElement>) => {
        if (p_.onClick) {
            p_.onClick((e.target as HTMLInputElement).value)
        }
    }

    const getSearchIconName = () => {
        if (p_.enableMobileSearch) {
            return p_.searchIsOpened ? 'close' : 'search'
        }

        return state.value ? 'close' : 'search'
    }

    const placeholder = isExist(p_.placeholder) ? p_.placeholder : translate('searchLabel')

    return (
        <div ref={searchRef} className={cn(styles.search)}>
            <EditText
                id={p_.id}
                className={cn(
                    styles.input,
                    p_.filter || p_.view || p_.sort ? '' : styles.fullRadius,
                    p_.className,
                    p_.enableMobileSearch && !p_.searchIsOpened
                        ? themeFromStore() !== 'smartplayer'
                            ? styles.hideTextNotSmartplayerTheme
                            : styles.hideText
                        : ''
                )}
                mod={p_.mod ? p_.mod : 'default'}
                type="text"
                value={state.value}
                placeholder={p_.isMobile && p_.enableMobileSearch ? '' : placeholder}
                onChange={(e: string) => changeSearch(e)}
                onFocus={onFocus}
                onBlur={onBlur}
                icon={p_.icon || getSearchIconName()}
                iconSize="16"
                iconColor={p_.activeAdvancedSearchIconColor || (state.active ? p_.iconActiveColor : p_.iconColor)}
                onIconClick={
                    p_.onSearchIconClick ||
                    ((_event: unknown, target: EditTextInputElement | null) => {
                        if (p_.searchToggle) {
                            p_.searchToggle()

                            return
                        }
                        if (target) clearFilter(target)
                    })
                }
                onClick={onClick}
                rounded={p_.rounded}
                autoComplete="off"
                iconTooltip={p_.iconTooltip}
                disableWheel={true}
                // fullHeight={true}
            />
            {p_.search && (
                <Autocomplete
                    {...p_.search.props}
                    onSelect={(element, type) => selectItem(element, type)}
                    isOpen={state.isOpen}
                />
            )}
            {/*{p_.searchElements}*/}
        </div>
    )
}

Search.defaultProps = {
    id: 'searchbar_search',
    iconColor: getThemeStyleValue('ui', 'icon', 'disable') || defaultThemeStyles.disable,
    iconActiveColor: getThemeStyleValue('ui', 'icon', 'active') || defaultThemeStyles.active,
    rounded: 'left_mini',
}

const mapStateToProps = (state: { app: { isMobile: boolean } }) => {
    return {
        isMobile: state.app.isMobile,
    }
}

export default connect(mapStateToProps, null, null, { forwardRef: true })(Search)
